import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

const MAX_STRING_CHARS = 24_000;
const MAX_ARRAY_ITEMS = 120;
const MAX_OBJECT_KEYS = 80;

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    fontFamily: theme.typography.fontFamily,
    backgroundColor: theme.palette.action.hover,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
  },
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    lineHeight: 1.5,
  },
  key: {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    fontSize: 13,
    minWidth: 'fit-content',
  },
  value: {
    color: theme.palette.text.primary,
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
    fontSize: 13,
  },
  bullet: {
    color: theme.palette.text.disabled,
    marginRight: theme.spacing(1),
    userSelect: 'none',
    fontSize: 13,
  },
  indent: {
    paddingLeft: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  emptyText: {
    fontStyle: 'italic',
    color: theme.palette.text.disabled,
    fontSize: 12,
  },
  blockText: {
    color: theme.palette.text.primary,
    fontSize: 13,
    lineHeight: 1.6,
    padding: theme.spacing(1, 1.5),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    marginTop: theme.spacing(0.5),
  },
  truncatedNote: {
    color: theme.palette.text.disabled,
    fontSize: 12,
    fontStyle: 'italic',
  },
}));

const truncateString = (
  s: string,
  max: number,
): { text: string; rest: number } => {
  if (s.length <= max) return { text: s, rest: 0 };
  return { text: s.slice(0, max), rest: s.length - max };
};

const DataNode: React.FC<{ value: unknown; name?: string }> = ({
  value,
  name,
}) => {
  const { classes } = useStyles();

  if (value === null || value === undefined) {
    return (
      <Box className={classes.row}>
        {name && <Typography className={classes.key}>{name}:</Typography>}
        <Typography className={classes.emptyText}>{String(value)}</Typography>
      </Box>
    );
  }

  if (typeof value === 'string' && value.includes('\n')) {
    const { text, rest } = truncateString(value, MAX_STRING_CHARS);
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        {name && <Typography className={classes.key}>{name}:</Typography>}
        <Typography className={classes.blockText}>{text}</Typography>
        {rest > 0 && (
          <Typography className={classes.truncatedNote}>
            … {rest} more characters (truncated for admin view)
          </Typography>
        )}
      </Box>
    );
  }

  if (typeof value !== 'object') {
    const raw =
      typeof value === 'string'
        ? truncateString(value, MAX_STRING_CHARS)
        : { text: String(value), rest: 0 };
    return (
      <Box className={classes.row}>
        {name && <Typography className={classes.key}>{name}:</Typography>}
        <Typography className={classes.value}>{raw.text}</Typography>
        {raw.rest > 0 && (
          <Typography className={classes.truncatedNote} component="span">
            {' '}
            (…+{raw.rest} chars)
          </Typography>
        )}
      </Box>
    );
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <Box className={classes.row}>
          {name && <Typography className={classes.key}>{name}:</Typography>}
          <Typography className={classes.emptyText}>[]</Typography>
        </Box>
      );
    }

    const isSimple = value.every(
      (v) =>
        v === null ||
        typeof v === 'string' ||
        typeof v === 'number' ||
        typeof v === 'boolean',
    );

    if (
      isSimple &&
      value.length <= 10 &&
      !value.some((v) => typeof v === 'string' && v.includes('\n'))
    ) {
      return (
        <Box className={classes.row}>
          {name && <Typography className={classes.key}>{name}:</Typography>}
          <Typography className={classes.value}>
            {value.map((v) => (v === null ? 'null' : String(v))).join(', ')}
          </Typography>
        </Box>
      );
    }

    const shown = value.slice(0, MAX_ARRAY_ITEMS);
    const hidden = value.length - shown.length;
    return (
      <Box sx={{ width: '100%' }}>
        {name && <Typography className={classes.key}>{name}:</Typography>}
        <Box className={name ? classes.indent : undefined}>
          {shown.map((item, i) => (
            <Box
              key={i}
              sx={{ display: 'flex', alignItems: 'flex-start', mt: 0.5 }}
            >
              <Typography className={classes.bullet}>-</Typography>
              <Box sx={{ flex: 1 }}>
                <DataNode value={item} />
              </Box>
            </Box>
          ))}
          {hidden > 0 && (
            <Typography className={classes.truncatedNote} sx={{ mt: 0.5 }}>
              … and {hidden} more items (truncated for admin view)
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  const keys = Object.keys(value as Record<string, unknown>);
  if (keys.length === 0) {
    return (
      <Box className={classes.row}>
        {name && <Typography className={classes.key}>{name}:</Typography>}
        <Typography className={classes.emptyText}>{'{}'}</Typography>
      </Box>
    );
  }

  const keySlice = keys.slice(0, MAX_OBJECT_KEYS);
  const keyHidden = keys.length - keySlice.length;
  return (
    <Box sx={{ width: '100%' }}>
      {name && <Typography className={classes.key}>{name}:</Typography>}
      <Box className={name ? classes.indent : undefined}>
        {keySlice.map((k) => (
          <DataNode
            key={k}
            name={k}
            value={(value as Record<string, unknown>)[k]}
          />
        ))}
        {keyHidden > 0 && (
          <Typography className={classes.truncatedNote}>
            … and {keyHidden} more keys (truncated for admin view)
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export const JsonViewer: React.FC<{ data: unknown }> = ({ data }) => {
  const { classes } = useStyles();

  const parsedData = useMemo(() => {
    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch {
        return data;
      }
    }
    return data;
  }, [data]);

  return (
    <Box className={classes.root}>
      <DataNode value={parsedData} />
    </Box>
  );
};
